import * as THREE from 'three';
import type { SfxName } from '../content/types';
import type { Controls } from '../core/device';
import { clamp, damp } from '../core/math';
import type { NpcHold } from '../entities/NpcManager';
import type { Player } from '../entities/Player';
import type { ArmWrestleGame } from '../minigames/armwrestle';
import type { MiniInput } from '../minigames/types';
import type { CharacterRig } from '../world/builders/character';
import type { Vec2 } from '../world/layout';
import type { Stage, StageButton, StageHud, StageInput } from './types';

export interface ArmSetup {
  /** Table center (world) and its height. */
  table: Vec2;
  tableY: number;
  /** Your seat and theirs; you face each other across the table. */
  you: Vec2;
  them: Vec2;
  seat: number;
  /** Whoever's across from you: an NPC (Big Tiny) or another player's avatar. */
  rival: Holdable | null;
  /** A table to set out for the match (an old cable spool), and where it goes. */
  prop?: { object: THREE.Object3D; parent: THREE.Object3D };
}

/** Anyone a stage can sit down and pose: NPCs and other players' avatars. */
export interface Holdable {
  hold: NpcHold | null;
  readonly rig: CharacterRig;
}

/** The struggle, in arm terms: shoulder roll toward the loser's side. */
function lockArm(rig: CharacterRig, arm: number, strain: number, time: number): void {
  rig.armR.rotation.z = 0.28 + arm * 0.55;
  rig.armR.rotation.x = -1.3 + strain * 0.08 * Math.sin(time * 31);
  rig.torso.rotation.z = -arm * 0.12;
  rig.head.rotation.z = arm * 0.1;
}

/**
 * Arm wrestling at the table, both of you seated with your elbows down and hands
 * locked. Mash to push; the hands tip toward whoever's losing.
 */
export class ArmStage implements Stage {
  readonly crosshair = false;
  readonly pointerLock = false;
  private t = 0;
  private shown = 0;
  private strain = 0;
  private lastPress = 0;
  private camA = 0;
  private readonly look = new THREE.Vector3();
  private baseFov = 60;
  private camera: THREE.PerspectiveCamera | null = null;

  constructor(
    readonly game: ArmWrestleGame,
    private readonly setup: ArmSetup,
    private readonly player: Player,
    private readonly sfx: (n: SfxName) => void,
  ) {}

  get title(): string {
    return this.game.title;
  }

  get done(): boolean {
    return this.game.done;
  }

  hint(controls: Controls): string {
    return this.game.hint(controls);
  }

  buttons(touch: boolean): StageButton[] {
    return touch ? [{ code: 'Space', label: ':flex:', primary: true }] : [];
  }

  private faceAcross(from: Vec2, to: Vec2): number {
    return Math.atan2(to.x - from.x, to.z - from.z);
  }

  begin(): void {
    const { setup, player } = this;
    if (setup.prop) {
      setup.prop.object.position.set(setup.table.x, 0, setup.table.z);
      setup.prop.parent.add(setup.prop.object);
    }
    player.teleport(setup.you.x, setup.you.z, this.faceAcross(setup.you, setup.table));
    player.scripted = true;
    player.anim.update({ dt: 1, time: 0, speed: 0, pose: 'armwrestle', seat: setup.seat });
    if (setup.rival) {
      setup.rival.hold = {
        x: setup.them.x,
        z: setup.them.z,
        heading: this.faceAcross(setup.them, setup.table),
        pose: 'armwrestle',
        seat: setup.seat,
        after: (rig, time) => lockArm(rig, -this.shown, this.strain, time),
      };
    }
    this.camA = Math.PI / 2;
  }

  update(dt: number, input: StageInput): void {
    this.t += dt;
    const g = this.game;
    const mini: MiniInput = {
      held: input.held,
      pressed: input.pressed,
      released: input.released,
      tap: input.tap,
      keys: input.keys,
    };
    if (mini.pressed || mini.tap) this.lastPress = this.t;
    g.update(dt, mini);
    this.shown = damp(this.shown, g.arm, 10, dt);
    this.strain = damp(this.strain, this.t - this.lastPress < 0.3 ? 1 : 0.3, 6, dt);
    this.player.anim.update({ dt, time: this.t, speed: 0, pose: 'armwrestle', seat: this.setup.seat });
    lockArm(this.player.rig, this.shown, this.strain, this.t);
    if (g.over && !this.ended) {
      this.ended = true;
      this.sfx(g.won ? 'crowd' : 'thud');
    }
  }

  private ended = false;

  frame(camera: THREE.PerspectiveCamera, dt: number): void {
    if (this.camera !== camera) {
      this.camera = camera;
      this.baseFov = camera.fov;
    }
    const s = this.setup;
    // Side-on to the table, easing in as it gets tense.
    this.camA += dt * 0.05;
    const tense = clamp(Math.abs(this.shown), 0, 1);
    const r = 3.1 - tense * 0.8;
    const ax = s.you.x - s.them.x;
    const az = s.you.z - s.them.z;
    const across = Math.atan2(az, ax);
    const a = across + this.camA;
    camera.position.set(
      s.table.x + Math.cos(a) * r,
      s.tableY + 0.95 - tense * 0.2,
      s.table.z + Math.sin(a) * r,
    );
    this.look.set(s.table.x, s.tableY + 0.3, s.table.z);
    camera.lookAt(this.look);
    if (camera.fov !== 50) {
      camera.fov = 50;
      camera.updateProjectionMatrix();
    }
  }

  hud(): StageHud {
    const g = this.game;
    return {
      score: g.over ? (g.won ? 'PINNED ’EM!' : 'PINNED') : 'MASH!',
      status: g.over ? '' : `vs. ${g.opponent}`,
      meters: [
        {
          label: ':flex:',
          value: (g.arm + 1) / 2,
          zone: [0.5, 1],
          marker: true,
          color: g.arm >= 0 ? '#9dff6a' : '#ff6b6b',
        },
      ],
    };
  }

  end(): void {
    if (this.camera) {
      this.camera.fov = this.baseFov;
      this.camera.updateProjectionMatrix();
    }
    const { player, setup } = this;
    setup.prop?.object.removeFromParent();
    if (setup.rival) {
      setup.rival.hold = null;
      setup.rival.rig.torso.rotation.z = 0;
      setup.rival.rig.head.rotation.z = 0;
    }
    player.rig.torso.rotation.z = 0;
    player.rig.head.rotation.z = 0;
    player.scripted = false;
    // Stand up beside the stool.
    const away = {
      x: setup.you.x + (setup.you.x - setup.table.x) * 0.6,
      z: setup.you.z + (setup.you.z - setup.table.z) * 0.6,
    };
    player.teleport(away.x, away.z, player.facing);
  }
}
